import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, ArrowRight, Heart, Shield, AlertCircle, Loader2 } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { partnerAdminApi, getPartnerSlug } from '../services/api'

export default function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('admin@silva-contadores.com.br')
  const [password, setPassword] = useState('Partner@123')
  const [showPassword, setShowPassword] = useState(false)
  const [partnerConfig, setPartnerConfig] = useState<any>(null)
  const [localError, setLocalError] = useState<string | null>(() => {
    // Recuperar erro do sessionStorage se existir (para sobreviver ao HMR)
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('partner_login_error')
    }
    return null
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mfaCode, setMfaCode] = useState('')

  const { login, verifyMfa, isLoading, error, clearError, isAuthenticated, mfaChallenge } = useAuthStore()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  // Load partner config for branding
  useEffect(() => {
    const loadPartnerConfig = async () => {
      try {
        const slug = getPartnerSlug()
        const config = await partnerAdminApi.auth.getConfig(slug)
        setPartnerConfig(config)
      } catch (err) {
        console.log('Could not load partner config, using defaults')
      }
    }
    loadPartnerConfig()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setLocalError(null)
    sessionStorage.removeItem('partner_login_error')
    setIsSubmitting(true)

    try {
      const challenge = await login(email, password)
      sessionStorage.removeItem('partner_login_error')
      if (!challenge) {
        navigate('/dashboard')
      }
    } catch (err: any) {
      // Set local error for immediate display
      const errorMessage = err.message || 'Erro ao fazer login. Tente novamente.'
      // Salvar no sessionStorage para sobreviver ao HMR
      sessionStorage.setItem('partner_login_error', errorMessage)
      setLocalError(errorMessage)
      console.error('Login failed:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setLocalError(null)
    setIsSubmitting(true)

    try {
      await verifyMfa(mfaCode)
      setMfaCode('')
      navigate('/dashboard')
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao validar 2FA.'
      sessionStorage.setItem('partner_login_error', errorMessage)
      setLocalError(errorMessage)
      console.error('MFA failed:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Combine errors from store and local state
  const displayError = localError || error

  const primaryColor = partnerConfig?.primaryColor || '#3B82F6'
  const companyName = partnerConfig?.companyName || 'NGC Medical'

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      {/* Left Panel - Logo and Branding */}
      <div className="hidden lg:flex lg:flex-1 items-center justify-center p-8 relative bg-gradient-to-br from-slate-900 to-slate-800">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-gradient-to-r from-emerald-500/15 to-cyan-500/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-gradient-to-r from-sky-500/15 to-emerald-500/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(14,165,233,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        </div>

        <div className="relative z-10 text-center max-w-md">
          <div className="mb-16">
            <div className="relative inline-block mb-12">
              <div className="relative">
                <div className="w-32 h-32 mx-auto bg-gradient-to-br from-sky-500 via-emerald-500 to-cyan-500 rounded-3xl flex items-center justify-center shadow-2xl">
                  <Shield className="w-16 h-16 text-white" />
                </div>
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <Heart className="w-5 h-5 text-white fill-white" />
                </div>
              </div>
            </div>

            <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-sky-100 to-emerald-100 bg-clip-text text-transparent mb-6">
              Partner Admin
            </h1>
            <p className="text-sky-200/80 text-2xl font-medium mb-4">
              {companyName}
            </p>
            <p className="text-slate-400 text-lg leading-relaxed max-w-sm mx-auto">
              Sistema de gestao para escritorios contabeis
            </p>
          </div>

          <div className="mt-20">
            <p className="text-slate-600 text-sm">
              © 2025 NGC Medical. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-r from-sky-500/10 to-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-sky-500/20 via-emerald-500/20 to-cyan-500/20 rounded-3xl blur-xl"></div>

            <div className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full"></div>

              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-white via-sky-100 to-emerald-100 bg-clip-text text-transparent mb-2">
                  Acesso Partner
                </h2>
                <p className="text-slate-400 text-sm">
                  Entre com suas credenciais
                </p>
              </div>

              {/* Error Alert */}
              {displayError && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl backdrop-blur-sm animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-300 text-sm font-medium">{displayError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={mfaChallenge ? handleMfaSubmit : handleSubmit} className="space-y-6">
                {mfaChallenge && (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-emerald-300" />
                      <p className="text-emerald-200 text-sm font-medium">
                        2FA ativado. Informe o codigo do seu app autenticador.
                      </p>
                    </div>
                  </div>
                )}

                {mfaChallenge ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white/90">
                      Codigo 2FA
                    </label>
                    <div className="relative group">
                      <input
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:border-emerald-400/50 transition-all duration-300 backdrop-blur-sm hover:bg-white/10"
                        placeholder="000000"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Email Field */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-white/90">
                        Email
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                          <Mail className="h-5 w-5 text-sky-400 group-focus-within:text-sky-300 transition-colors" />
                        </div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 focus:border-sky-400/50 transition-all duration-300 backdrop-blur-sm hover:bg-white/10"
                          placeholder="seu@email.com"
                          required
                        />
                      </div>
                    </div>

                    {/* Password Field */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-white/90">
                        Senha
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                          <Lock className="h-5 w-5 text-sky-400 group-focus-within:text-sky-300 transition-colors" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 focus:border-sky-400/50 transition-all duration-300 backdrop-blur-sm hover:bg-white/10 font-sans"
                          placeholder="••••••••"
                          required
                          autoComplete="current-password"
                          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-sky-400 transition-colors z-10"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading || isSubmitting}
                    className="relative w-full group overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-sky-500 via-emerald-500 to-cyan-500 rounded-2xl blur-lg opacity-70 group-hover:opacity-100 transition-all duration-500"></div>

                    <div className="relative bg-gradient-to-r from-sky-500 via-emerald-500 to-cyan-500 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-300 group-hover:scale-[1.02] group-active:scale-[0.98] shadow-2xl">
                      <div className="relative flex items-center justify-center gap-3">
                        {(isLoading || isSubmitting) ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-lg font-bold">
                              {mfaChallenge ? 'Validando...' : 'Entrando...'}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-lg font-bold">
                              {mfaChallenge ? 'Validar 2FA' : 'Entrar'}
                            </span>
                            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              </form>

              {mfaChallenge && (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="mt-4 w-full text-sm text-slate-300 hover:text-white transition-colors"
                >
                  Voltar para login com senha
                </button>
              )}

              {/* Credentials Helper */}
              <div className="mt-6 p-3 bg-sky-500/5 border border-sky-500/10 rounded-xl backdrop-blur-sm">
                <p className="text-sky-300 text-xs font-medium mb-1">Credenciais de Teste</p>
                <p className="text-xs text-slate-400">Email: admin@silva-contadores.com.br</p>
                <p className="text-xs text-slate-400">Senha: Partner@123</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
